import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react'
import { User, Session, Provider } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'

interface UserProfile {
  id: string
  username: string
  avatar_url: string | null
  avatar_id: string | null
  bio: string | null
  role: string
  reputation: number
  created_at: string
  is_verified_creator?: boolean
}

interface AuthContextType {
  user: User | null
  profile: UserProfile | null
  session: Session | null
  loading: boolean
  initializing: boolean
  isAuthenticated: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, username: string) => Promise<void>
  signOut: () => Promise<void>
  signInWithProvider: (provider: Provider) => Promise<void>
  resetPassword: (email: string) => Promise<void>
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ error?: any }>
  createProfile: (username: string) => Promise<{ error?: any }>
  checkUsernameAvailability: (username: string) => Promise<boolean>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Cache for profile data to prevent unnecessary refetches
const profileCache = new Map<string, { data: UserProfile; timestamp: number }>()
const CACHE_DURATION = 60000 // 1 minute cache

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(false)
  const [initializing, setInitializing] = useState(true)
  
  // Refs to track component mount state and prevent race conditions
  const mountedRef = useRef(true)
  const fetchingProfileRef = useRef(false)
  const lastFetchedUserIdRef = useRef<string | null>(null)

  // Fetch user profile with caching and retry logic
  const fetchProfile = useCallback(async (userId: string, forceRefresh = false): Promise<UserProfile | null> => {
    // Prevent concurrent fetches for the same user
    if (fetchingProfileRef.current && lastFetchedUserIdRef.current === userId && !forceRefresh) {
      return profile
    }

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = profileCache.get(userId)
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        if (mountedRef.current) {
          setProfile(cached.data)
        }
        return cached.data
      }
    }

    fetchingProfileRef.current = true
    lastFetchedUserIdRef.current = userId

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle()

      if (error) {
        console.error('Error fetching profile:', error)
        // Retry once after a short delay
        await new Promise(resolve => setTimeout(resolve, 500))
        const { data: retryData, error: retryError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle()
        
        if (retryError || !retryData) {
          console.error('Retry failed:', retryError)
          return null
        }
        
        // Update cache and state
        profileCache.set(userId, { data: retryData, timestamp: Date.now() })
        if (mountedRef.current) {
          setProfile(retryData)
        }
        return retryData
      }
      
      if (data) {
        // Update cache and state
        profileCache.set(userId, { data, timestamp: Date.now() })
        if (mountedRef.current) {
          setProfile(data)
        }
        return data
      }
      
      return null
    } catch (error) {
      console.error('Error fetching profile:', error)
      return null
    } finally {
      fetchingProfileRef.current = false
    }
  }, [profile])

  // Refresh profile function (force refresh)
  const refreshProfile = useCallback(async () => {
    if (user?.id) {
      await fetchProfile(user.id, true)
    }
  }, [user?.id, fetchProfile])

  // Initialize auth state
  useEffect(() => {
    mountedRef.current = true
    let authSubscription: { unsubscribe: () => void } | null = null

    const initializeAuth = async () => {
      try {
        // Check if user explicitly signed out
        const signedOut = sessionStorage.getItem('user-signed-out')
        if (signedOut) {
          setInitializing(false)
          return
        }

        // Get initial session
        const { data: { session: initialSession }, error } = await supabase.auth.getSession()
        
        if (error) {
          console.error('Error getting session:', error)
          setInitializing(false)
          return
        }

        if (!mountedRef.current) return

        if (initialSession?.user) {
          setSession(initialSession)
          setUser(initialSession.user)
          
          // Fetch profile
          await fetchProfile(initialSession.user.id)
        }
        
        setInitializing(false)
      } catch (error) {
        console.error('Auth initialization error:', error)
        if (mountedRef.current) {
          setInitializing(false)
        }
      }
    }

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (!mountedRef.current) return

      // Handle sign out event
      if (event === 'SIGNED_OUT') {
        setSession(null)
        setUser(null)
        setProfile(null)
        profileCache.clear()
        return
      }

      // Handle sign in events
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        if (newSession?.user) {
          setSession(newSession)
          setUser(newSession.user)
          
          // Clear sign out flag
          sessionStorage.removeItem('user-signed-out')
          
          // Fetch profile (force refresh on sign in)
          const shouldForceRefresh = event === 'SIGNED_IN'
          await fetchProfile(newSession.user.id, shouldForceRefresh)
        }
        return
      }

      // Handle initial session
      if (event === 'INITIAL_SESSION') {
        if (newSession?.user) {
          setSession(newSession)
          setUser(newSession.user)
          await fetchProfile(newSession.user.id)
        }
      }
    })

    authSubscription = subscription

    // Initialize
    initializeAuth()

    return () => {
      mountedRef.current = false
      authSubscription?.unsubscribe()
    }
  }, []) // Empty dependency array - only run once on mount

  // Sign in
  const signIn = useCallback(async (email: string, password: string) => {
    setLoading(true)
    try {
      // Clear sign out flag
      sessionStorage.removeItem('user-signed-out')
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error

      // Profile will be fetched by the auth state listener
    } catch (error) {
      console.error('Error signing in:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Sign in with OAuth provider
  const signInWithProvider = useCallback(async (provider: Provider) => {
    try {
      // Clear sign out flag
      sessionStorage.removeItem('user-signed-out')
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      })

      if (error) throw error
    } catch (error) {
      console.error('Error signing in with provider:', error)
      throw error
    }
  }, [])

  // Sign up
  const signUp = useCallback(async (
    email: string,
    password: string,
    username: string
  ) => {
    setLoading(true)
    try {
      // Check username availability
      const isAvailable = await checkUsernameAvailability(username)
      if (!isAvailable) {
        throw new Error('Username is already taken')
      }

      // Sign up user
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username,
          },
        },
      })

      if (error) throw error

      // Wait for session to be established
      if (data.user && data.session) {
        // Session is established, now create profile
        const { error: profileError } = await supabase
          .from('user_profiles')
          .insert({
            id: data.user.id,
            username: username.toLowerCase(),
            role: 'user',
            reputation: 0,
          })

        if (profileError) {
          console.error('Profile creation error:', profileError)
          throw new Error('Failed to create user profile. Please try again.')
        }

        // Clear sign out flag
        sessionStorage.removeItem('user-signed-out')
        
        // Profile will be fetched by auth state listener
      } else if (data.user && !data.session) {
        console.log('Email confirmation required.')
      }
    } catch (error) {
      console.error('Error signing up:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Sign out
  const signOut = useCallback(async () => {
    setLoading(true)
    try {
      // Set sign out flag FIRST
      sessionStorage.setItem('user-signed-out', 'true')
      
      // Clear profile cache
      profileCache.clear()
      
      // Clear state immediately
      setUser(null)
      setProfile(null)
      setSession(null)
      
      // Clear all auth tokens from storage
      const keysToRemove = [
        'sb-auth-token',
        'sb-auth-token-backup',
        'sb-auth-token-backup-2',
        'sb-auth-backup-timestamp',
        'backup_sb-auth-token'
      ]
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(key)
        sessionStorage.removeItem(key)
      })

      // Sign out from Supabase
      await supabase.auth.signOut({ scope: 'local' })
    } catch (error) {
      console.error('Error signing out:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Reset password
  const resetPassword = useCallback(async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/callback`,
      })
      if (error) throw error
    } catch (error) {
      console.error('Error resetting password:', error)
      throw error
    }
  }, [])

  // Username availability check
  const checkUsernameAvailability = useCallback(async (username: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('username')
        .eq('username', username.toLowerCase())
        .maybeSingle()

      if (error && error.code === 'PGRST116') {
        return true
      }

      if (error) {
        console.error('Error checking username:', error)
        return false
      }

      return !data
    } catch (error) {
      console.error('Error checking username availability:', error)
      return false
    }
  }, [])

  // Update profile
  const updateProfile = useCallback(async (updates: Partial<UserProfile>) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    setLoading(true)
    try {
      const updateData: Record<string, any> = {}
      
      if (updates.username !== undefined) {
        updateData.username = updates.username.toLowerCase()
      }
      if (updates.bio !== undefined) {
        updateData.bio = updates.bio
      }
      if (updates.avatar_id !== undefined) {
        updateData.avatar_id = updates.avatar_id
      }
      if (updates.avatar_url !== undefined) {
        updateData.avatar_url = updates.avatar_url
      }
      
      updateData.updated_at = new Date().toISOString()

      const { error } = await supabase
        .from('user_profiles')
        .update(updateData)
        .eq('id', user.id)

      if (error) return { error }

      // Force refresh profile after update
      await fetchProfile(user.id, true)
      return {}
    } catch (error) {
      console.error('Error updating profile:', error)
      return { error }
    } finally {
      setLoading(false)
    }
  }, [user, fetchProfile])

  // Create profile
  const createProfile = useCallback(async (username: string) => {
    if (!user) return { error: { message: 'No user logged in' } }

    setLoading(true)
    try {
      // Check if profile already exists
      const existingProfile = await fetchProfile(user.id)
      if (existingProfile) {
        return { error: { message: 'Profile already exists' } }
      }

      // Check username availability
      const isAvailable = await checkUsernameAvailability(username)
      if (!isAvailable) {
        return { error: { message: 'Username is already taken' } }
      }

      const { error } = await supabase
        .from('user_profiles')
        .insert({
          id: user.id,
          username: username.toLowerCase(),
          role: 'user',
          reputation: 0,
        })

      if (error) return { error }

      // Force refresh profile
      await fetchProfile(user.id, true)
      return {}
    } catch (error) {
      console.error('Error creating profile:', error)
      return { error }
    } finally {
      setLoading(false)
    }
  }, [user, fetchProfile, checkUsernameAvailability])

  const value: AuthContextType = {
    user,
    profile,
    session,
    loading,
    initializing,
    isAuthenticated: !!user && !!session,
    signIn,
    signUp,
    signOut,
    signInWithProvider,
    resetPassword,
    updateProfile,
    createProfile,
    checkUsernameAvailability,
    refreshProfile,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Export the useAuth hook for consuming the context
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
